import type { CheckboxV2Props } from '.';
import { animationTransition } from '@shared/utils/animationTransition';
import { Box } from '../Box';
import { styled } from '@withjoy/joykit/theme';

import { anatomy } from '@shared/joykit/packages/core/common/utils/anatomy';
import type { MultiPartStyleConfig, PartsStyleObject } from '@shared/joykit/packages/core/common/utils/styleConfig';

export const StyledRoot = styled(Box)``;

export const StyledControl = styled(Box)``;

export const StyledInput = styled(Box)``;

export const StyledLabel = styled(Box)``;

const checkboxAnatomy = anatomy('Checkbox').parts('root', 'control', 'label');

export const visuallyHiddenStyle: React.CSSProperties = {
  border: '0px',
  clip: 'rect(0px, 0px, 0px, 0px)',
  height: '1px',
  width: '1px',
  margin: '-1px',
  padding: '0px',
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  position: 'absolute',
};

const checkboxBaseStyles: PartsStyleObject<typeof checkboxAnatomy> = {
  root: {
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    verticalAlign: 'top',
    position: 'relative',
    _disabled: {
      cursor: 'not-allowed',
    },
  },
  control: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    verticalAlign: 'top',
    userSelect: 'none',
    flexShrink: 0,
    borderRadius: 2,
    size: '1.25rem',
    border: '1px solid',
    borderColor: 'mono5',
    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
    transition: animationTransition('border-color', 'background-color', 'color', 'box-shadow'),
    _hover: {
      borderColor: 'mono8',
    },
    _checked: {
      backgroundColor: 'mono14',
      borderColor: 'transparent',
      color: 'white',
    },
    _indeterminate: {
      backgroundColor: 'mono14',
      borderColor: 'transparent',
      color: 'white',
    },
    _disabled: {
      backgroundColor: 'mono2',
      borderColor: 'mono3',
      color: 'mono3',
    },
    /*
     * Was a translucent 3px halo on `_focus`, which fired on mouse click as well as keyboard and sat
     * at a different weight from every field's ring. `_focusVisible` and 2px bring it onto the
     * shared contract; the offset is what keeps it visible once the control fills with mono14, where
     * a flush ring in the same colour would vanish. See doc/field-focus-consistency/PLAN.md.
     */
    _focusVisible: {
      outline: '2px solid',
      outlineColor: 'inputBorderFocused',
      outlineOffset: '2px',
    },
  },
  label: {
    transition: animationTransition('opacity'),
    userSelect: 'none',
    _disabled: { opacity: 0.4 },
  },
};

export const checkboxStyles: MultiPartStyleConfig<CheckboxV2Props, typeof checkboxAnatomy> = {
  parts: checkboxAnatomy.keys,
  baseStyle: checkboxBaseStyles,
};
